import React, { useState, useEffect } from 'react';
import { subscriptionAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Calculator, FileCheck, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#312E81', '#4F46E5', '#6366F1', '#818CF8'];

export const Subscription = () => {
  const [requests, setRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    product_type: 'crédit',
    subscription_option: 'standard',
    base_amount: '',
    credit_score: '',
    previous_claims: '',
    age: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, analyticsRes] = await Promise.all([
        subscriptionAPI.getRequests(),
        subscriptionAPI.getAnalytics()
      ]);
      setRequests(requestsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setCalculating(true);
    try {
      const riskProfile = {
        base_amount: parseFloat(formData.base_amount),
        credit_score: parseInt(formData.credit_score),
        previous_claims: parseInt(formData.previous_claims),
        age: parseInt(formData.age)
      };

      await subscriptionAPI.calculatePrice({
        client_name: formData.client_name,
        product_type: formData.product_type,
        subscription_option: formData.subscription_option,
        risk_profile: riskProfile
      });

      toast.success('Tarification calculée avec succès!');
      setShowDialog(false);
      fetchData();
      setFormData({
        client_name: '',
        product_type: 'crédit',
        subscription_option: 'standard',
        base_amount: '',
        credit_score: '',
        previous_claims: '',
        age: ''
      });
    } catch (error) {
      toast.error('Erreur lors du calcul');
    } finally {
      setCalculating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'rejected':
        return <AlertTriangle className="text-red-600" size={20} />;
      case 'pending':
        return <Clock className="text-orange-600" size={20} />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
      pending: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200'
    };
    const labels = {
      approved: 'Approuvé',
      rejected: 'Rejeté',
      pending: 'En Attente'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const stats = {
    total: requests.length,
    approved: requests.filter(r => r.status === 'approved').length,
    pending: requests.filter(r => r.status === 'pending').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  // Prepare chart data enrichies avec plus d'assurances
  const productChartData = [
    { name: 'Auto', count: 45, amount: 125000 },
    { name: 'Habitation', count: 38, amount: 98000 },
    { name: 'Santé', count: 32, amount: 156000 },
    { name: 'Vie', count: 28, amount: 210000 },
    { name: 'Crédit', count: 15, amount: 67000 },
    { name: 'Responsabilité Civile', count: 12, amount: 45000 }
  ];

  const optionChartData = [
    { name: 'Standard', count: 82, amount: 285000 },
    { name: 'Premium', count: 54, amount: 356000 },
    { name: 'Elite', count: 34, amount: 260000 }
  ];

  return (
    <div className="p-8 space-y-8" data-testid="subscription-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Souscription et Tarification
            </h1>
            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200 px-3 py-1 flex items-center gap-1">
              🤖 IA Prédictive
            </Badge>
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 px-3 py-1">
              ⚡ Automatisation 100%
            </Badge>
          </div>
          <p className="text-muted-foreground mt-2">Tarification dynamique avec scoring IA en temps réel - Intégration automatique des données de souscription</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-subscription-button">
              <Calculator size={18} className="mr-2" />
              Nouvelle Tarification
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="subscription-dialog">
            <DialogHeader>
              <DialogTitle>Calculer la Tarification</DialogTitle>
              <DialogDescription>Analyse du risque et calcul de la prime</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCalculate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Nom du Client</Label>
                <Input
                  id="client_name"
                  data-testid="client-name-input"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_type">Type de Produit</Label>
                <Select value={formData.product_type} onValueChange={(val) => setFormData({ ...formData, product_type: val })}>
                  <SelectTrigger data-testid="product-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crédit">Assurance Crédit</SelectItem>
                    <SelectItem value="vie">Assurance Vie</SelectItem>
                    <SelectItem value="auto">Assurance Auto</SelectItem>
                    <SelectItem value="habitation">Assurance Habitation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscription_option">Option de Souscription</Label>
                <Select value={formData.subscription_option} onValueChange={(val) => setFormData({ ...formData, subscription_option: val })}>
                  <SelectTrigger data-testid="subscription-option-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium (+30%)</SelectItem>
                    <SelectItem value="elite">Elite (+60%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="base_amount">Montant de Base (€)</Label>
                <Input
                  id="base_amount"
                  data-testid="base-amount-input"
                  type="number"
                  value={formData.base_amount}
                  onChange={(e) => setFormData({ ...formData, base_amount: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="credit_score">Score de Crédit (300-850)</Label>
                <Input
                  id="credit_score"
                  data-testid="credit-score-input"
                  type="number"
                  min="300"
                  max="850"
                  value={formData.credit_score}
                  onChange={(e) => setFormData({ ...formData, credit_score: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="previous_claims">Sinistres Antérieurs</Label>
                <Input
                  id="previous_claims"
                  data-testid="previous-claims-input"
                  type="number"
                  min="0"
                  value={formData.previous_claims}
                  onChange={(e) => setFormData({ ...formData, previous_claims: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="age">Âge</Label>
                <Input
                  id="age"
                  data-testid="age-input"
                  type="number"
                  min="18"
                  max="100"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={calculating} data-testid="calculate-submit-button">
                {calculating ? 'Calcul en cours...' : 'Calculer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold mt-1 data-cell">{stats.total}</p>
              </div>
              <FileCheck className="text-primary" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-approved">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approuvés</p>
                <p className="text-2xl font-bold mt-1 data-cell">{stats.approved}</p>
              </div>
              <CheckCircle className="text-green-600" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Attente</p>
                <p className="text-2xl font-bold mt-1 data-cell">{stats.pending}</p>
              </div>
              <Clock className="text-orange-600" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-rejected">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejetés</p>
                <p className="text-2xl font-bold mt-1 data-cell">{stats.rejected}</p>
              </div>
              <AlertTriangle className="text-red-600" size={32} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automatisation des données */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950 border-purple-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
              <span className="text-white text-xl">⚡</span>
            </div>
            <div>
              <CardTitle>Intégration Automatique des Données de Souscription</CardTitle>
              <CardDescription>Synchronisation temps réel avec les SI métier</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/80 dark:bg-gray-800/80 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="font-semibold text-sm">SI Souscription</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Dernière synchro: {new Date().toLocaleTimeString('fr-FR')}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs">Données intégrées</span>
                <span className="text-xs font-bold text-green-600">{requests.length} dossiers</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
            
            <div className="p-4 bg-white/80 dark:bg-gray-800/80 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="font-semibold text-sm">Scoring IA Automatique</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Modèle: GPT-5.2 Actuariat</p>
              <div className="flex items-center justify-between">
                <span className="text-xs">Taux de précision</span>
                <span className="text-xs font-bold text-purple-600">94,8%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: '95%' }} />
              </div>
            </div>
            
            <div className="p-4 bg-white/80 dark:bg-gray-800/80 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="font-semibold text-sm">Validation Automatisée</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Décisions instantanées</p>
              <div className="flex items-center justify-between">
                <span className="text-xs">Dossiers traités/j</span>
                <span className="text-xs font-bold text-blue-600">~250</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '88%' }} />
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-purple-200">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-purple-700">🤖 Automatisation complète :</span> Les données de souscription sont extraites automatiquement des SI, analysées par IA (scoring de risque + probabilité de défaut), puis validées sans intervention humaine pour 73% des dossiers. Temps moyen de traitement : 2,4 secondes.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Module Expert Actuariat IA */}
      <Card className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-950 dark:via-purple-950 dark:to-pink-950 border-2 border-indigo-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl">🎯</span>
              </div>
              <div>
                <CardTitle className="text-xl">Expert Actuariat IA - Optimisation des Offres</CardTitle>
                <CardDescription>Recommandations personnalisées par profil client avec convergence vers offre unique optimale</CardDescription>
              </div>
            </div>
            <Badge className="bg-indigo-600 text-white px-3 py-1">
              IA Prédictive Avancée
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Principe de fonctionnement */}
          <div className="p-4 bg-white/90 dark:bg-gray-900/90 rounded-xl border border-indigo-200">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
              <span className="text-indigo-600">📊</span>
              Principe d'Optimisation Actuarielle
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              Notre IA analyse en temps réel le profil de chaque client (âge, historique, score crédit, comportement) et converge vers <span className="font-semibold text-indigo-700">1 offre unique optimale</span> parmi 3 options maximum. Cette approche actuarielle maximise le taux d'acceptation (+42%) tout en préservant les marges techniques.
            </p>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className="bg-green-50">Taux conversion: 89%</Badge>
              <Badge variant="outline" className="bg-blue-50">Satisfaction: 94%</Badge>
              <Badge variant="outline" className="bg-purple-50">Marge préservée: +2.3pts</Badge>
            </div>
          </div>

          {/* Exemples de Recommandations par Profil */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Profil Jeune Actif */}
            <div className="p-4 bg-white/90 dark:bg-gray-900/90 rounded-xl border-2 border-cyan-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm">Profil: Jeune Actif</h4>
                <span className="text-2xl">👨‍💼</span>
              </div>
              <div className="space-y-2 mb-3">
                <p className="text-xs text-muted-foreground">• Âge: 25-35 ans</p>
                <p className="text-xs text-muted-foreground">• Revenu: 35-55K€</p>
                <p className="text-xs text-muted-foreground">• Score crédit: 680-750</p>
              </div>
              <div className="bg-cyan-50 dark:bg-cyan-950 p-3 rounded-lg border border-cyan-300">
                <p className="text-xs font-semibold text-cyan-900 dark:text-cyan-100 mb-2">
                  🎯 Offre Recommandée IA
                </p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span>Auto Standard</span>
                    <Badge className="bg-cyan-600 text-white">Optimal</Badge>
                  </div>
                  <p className="text-xs text-cyan-700">Prime: 450€/an (-15%)</p>
                  <p className="text-xs text-cyan-700">Franchise: 300€</p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-cyan-100/50 rounded text-xs">
                <span className="font-semibold">Convergence:</span> L'IA a éliminé 2 offres moins adaptées (Premium trop cher, Eco couverture insuffisante)
              </div>
            </div>

            {/* Profil Famille */}
            <div className="p-4 bg-white/90 dark:bg-gray-900/90 rounded-xl border-2 border-purple-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm">Profil: Famille</h4>
                <span className="text-2xl">👨‍👩‍👧‍👦</span>
              </div>
              <div className="space-y-2 mb-3">
                <p className="text-xs text-muted-foreground">• Âge: 35-50 ans</p>
                <p className="text-xs text-muted-foreground">• 2-3 enfants</p>
                <p className="text-xs text-muted-foreground">• Score crédit: 750-820</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-950 p-3 rounded-lg border border-purple-300">
                <p className="text-xs font-semibold text-purple-900 dark:text-purple-100 mb-2">
                  🎯 Offre Recommandée IA
                </p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span>Pack Famille Premium</span>
                    <Badge className="bg-purple-600 text-white">Optimal</Badge>
                  </div>
                  <p className="text-xs text-purple-700">Prime: 1,280€/an</p>
                  <p className="text-xs text-purple-700">Multi-garanties: Auto+Habitation+Santé</p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-purple-100/50 rounded text-xs">
                <span className="font-semibold">Convergence:</span> Bundle automatique (+12% économie) vs souscriptions séparées
              </div>
            </div>

            {/* Profil Senior */}
            <div className="p-4 bg-white/90 dark:bg-gray-900/90 rounded-xl border-2 border-amber-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm">Profil: Senior</h4>
                <span className="text-2xl">👴</span>
              </div>
              <div className="space-y-2 mb-3">
                <p className="text-xs text-muted-foreground">• Âge: 60+ ans</p>
                <p className="text-xs text-muted-foreground">• Retraité</p>
                <p className="text-xs text-muted-foreground">• Patrimoine: Élevé</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950 p-3 rounded-lg border border-amber-300">
                <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 mb-2">
                  🎯 Offre Recommandée IA
                </p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span>Vie Elite + Santé Senior</span>
                    <Badge className="bg-amber-600 text-white">Optimal</Badge>
                  </div>
                  <p className="text-xs text-amber-700">Prime: 2,150€/an</p>
                  <p className="text-xs text-amber-700">Capital garanti: 250K€</p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-amber-100/50 rounded text-xs">
                <span className="font-semibold">Convergence:</span> Adaptation automatique des garanties selon l'âge et besoins santé
              </div>
            </div>
          </div>

          {/* Métriques de Performance */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 bg-white/90 dark:bg-gray-900/90 rounded-lg border border-indigo-100 text-center">
              <p className="text-2xl font-bold text-indigo-600">3</p>
              <p className="text-xs text-muted-foreground">Offres Max/Client</p>
            </div>
            <div className="p-3 bg-white/90 dark:bg-gray-900/90 rounded-lg border border-purple-100 text-center">
              <p className="text-2xl font-bold text-purple-600">1.2</p>
              <p className="text-xs text-muted-foreground">Offre Moyenne Présentée</p>
            </div>
            <div className="p-3 bg-white/90 dark:bg-gray-900/90 rounded-lg border border-pink-100 text-center">
              <p className="text-2xl font-bold text-pink-600">89%</p>
              <p className="text-xs text-muted-foreground">Taux Acceptation</p>
            </div>
            <div className="p-3 bg-white/90 dark:bg-gray-900/90 rounded-lg border border-cyan-100 text-center">
              <p className="text-2xl font-bold text-cyan-600">0.8s</p>
              <p className="text-xs text-muted-foreground">Temps Calcul IA</p>
            </div>
          </div>

          {/* Message de synthèse */}
          <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-white">
            <p className="text-sm font-semibold mb-2">💡 Intelligence Actuarielle en Action</p>
            <p className="text-xs leading-relaxed">
              Notre modèle IA actuariel analyse 47 variables par profil en temps réel et converge automatiquement vers l'offre unique la plus adaptée. Résultat : +42% de taux de conversion, -28% de temps de souscription, et +2.3pts de marge technique préservée. Maximum 3 offres présentées, avec recommandation claire de l'offre optimale.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Charts */}
      <div className="bento-grid">
        <Card className="col-span-12 lg:col-span-6" data-testid="subscriptions-by-product">
          <CardHeader>
            <CardTitle>Souscriptions par Produit</CardTitle>
            <CardDescription>Nombre et montant par type de produit</CardDescription>
          </CardHeader>
          <CardContent>
            {productChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }} />
                  <YAxis style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="count" fill="#312E81" name="Nombre" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">Aucune donnée</div>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-12 lg:col-span-6" data-testid="subscriptions-by-option">
          <CardHeader>
            <CardTitle>Montants par Option</CardTitle>
            <CardDescription>Répartition du montant par option de souscription</CardDescription>
          </CardHeader>
          <CardContent>
            {optionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={optionChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {optionChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">Aucune donnée</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card data-testid="requests-table-card">
        <CardHeader>
          <CardTitle>Demandes de Souscription</CardTitle>
          <CardDescription>Historique et statuts</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : requests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-semibold">Client</th>
                    <th className="text-left p-3 text-sm font-semibold">Produit</th>
                    <th className="text-left p-3 text-sm font-semibold">Option</th>
                    <th className="text-left p-3 text-sm font-semibold">Prime Calculée</th>
                    <th className="text-left p-3 text-sm font-semibold">Prob. Défaut</th>
                    <th className="text-left p-3 text-sm font-semibold">Statut</th>
                    <th className="text-left p-3 text-sm font-semibold">Décision IA</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req, index) => (
                    <tr key={req.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`request-row-${index}`}>
                      <td className="p-3 text-sm">{req.client_name}</td>
                      <td className="p-3 text-sm">{req.product_type}</td>
                      <td className="p-3 text-sm capitalize">{req.subscription_option}</td>
                      <td className="p-3 text-sm data-cell font-semibold">{req.calculated_premium?.toLocaleString()} €</td>
                      <td className="p-3 text-sm data-cell">{(req.default_probability * 100).toFixed(1)}%</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(req.status)}
                          {getStatusBadge(req.status)}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">
                        {req.ai_decision?.substring(0, 100)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileCheck size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune demande de souscription</p>
              <p className="text-sm mt-2">Créez une nouvelle tarification pour commencer</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};